import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Tabs,
  Tab,
  CircularProgress,
  IconButton,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";


import { SectionHeader } from "./SectionHeader";
import { useSelector } from "react-redux";
import type { RootState } from "@/store/rootReducer";
import { useAppDispatch } from "@/store/hooks";
import {
  getMostOrderedProducts,
  getOpportunityMetrics,
  getOrderMetrics,
  getReferralBussiness,
  getTaskMetrics,
  getVisitsMetrics,
} from "@/store/slices/dashboard360Slice";
import { usePermissions } from "@/hooks/usePermissions";
import OpportunityMetrics from "./OpportunityMetrics";
import OrderMetrics from "./OrderMetrics";
import TaskMetrics from "./TaskMetrics";
import Referral from "./Referral";
import Cookies from "js-cookie";

interface TableColumn {
  name: string;
  selector: (row: Record<string, any>) => string | number;
  sortable?: boolean;
}

const Insights: React.FC<{ LeadId: number; fullHeight?: boolean }> = ({ LeadId, fullHeight }) => {
  const dispatch = useAppDispatch();
  const [tab, setTab] = useState<"normal" | "financial">("normal");
  const isFetching = useSelector(
    (state: RootState) => state.dashboard360.isFetching
  );
  const [isFinancialYear, SetIsFinancialYear] = useState<boolean>(false);
  const Data = useSelector((state: RootState) => state.dashboard360);

  const { currentUser, permissionLoading } = usePermissions();

  const currencySymbol =
    Cookies.get("mobile")
      ? JSON.parse(Cookies.get("mobile")!).currencySymbol
      : currentUser?.CompanyDetail?.CurrencySymbol ?? "";

  const columns: TableColumn[] = [
    { name: "Emp No.", selector: (row) => row.EmployeeNo },
    { name: "Name", selector: (row) => row.UserName },
    { name: "Date & Time", selector: (row) => row.DateAndTime },
    { name: "Duration (min)", selector: (row) => row.Duration },
    { name: "Status", selector: (row) => row.StatusName },
  ];
  const opportunityFollowUpsColumns: TableColumn[] = [
    { name: "Opp No.", selector: (row) => row.OpportunityNumber },
    { name: "Opp Name", selector: (row) => row.OpportunityName },
    { name: "Tag", selector: (row) => row.Tags },
    {
      name: `Value ${currencySymbol}`,
      selector: (row) => row.Value,
    },
    { name: "Updated since", selector: (row) => row.LastUpdatedSince },
    { name: "By", selector: (row) => row.FollowUpBy },
  ];
  const lostReasonColumns: TableColumn[] = [
    { name: "Reason", selector: (row) => row.LossReason },
    { name: "Count", selector: (row) => row.LostOpportunityCount },
    {
      name: `Value ${currencySymbol}`,
      selector: (row) => row.TotalLostValue,
    },
  ];
  const upcomingVisitsColumn: TableColumn[] = [
    { name: "Emp No.", selector: (row) => row.EmployeeNo },
    { name: "User Name", selector: (row) => row.UserName },
    { name: "Date & Time", selector: (row) => row.RosterPlaningDate },
  ];
  const mostOrderedColumn: TableColumn[] = [
    { name: "Product", selector: (row) => row.ProductName, sortable: true },
    { name: "SKU Code", selector: (row) => row.ProductSqlCode, sortable: true },
    { name: "Quantity", selector: (row) => row.TotalQuantity, sortable: true },
    {
      name: `Amount(${currencySymbol})`,
      selector: (row) => row.TotalAmount,
      sortable: true,
    },
  ];

  const handleChange = (
    _: React.SyntheticEvent,
    newValue: "normal" | "financial"
  ) => {
    setTab(newValue);

    if (newValue === "financial") {
      SetIsFinancialYear(true);
    } else {
      SetIsFinancialYear(false);
    }
  };

  const fetchMetrics = useCallback(() => {
    const params = { isFinancialYear };

    dispatch(getVisitsMetrics({ id: LeadId, params }));
    dispatch(getOpportunityMetrics({ id: LeadId, params }));
    dispatch(getOrderMetrics({ id: LeadId, params }));
    dispatch(getTaskMetrics({ id: LeadId, params }));
    dispatch(getMostOrderedProducts({ id: LeadId, params }));
    dispatch(getReferralBussiness({ id: LeadId, params }));
  }, [isFinancialYear, dispatch, LeadId]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);
  const visitLabels =
    Data?.data?.metricsData?.VisitCount?.map((v: { Year: any }) => v.Year) ||
    [];
  const visitDatasets = [
    {
      label: "Total Visits",
      data:
        Data?.data?.metricsData?.VisitCount?.map(
          (v: { TotalVisits: any }) => v.TotalVisits
        ) || [],
      backgroundColor: "#2196f3",
    },
    {
      label: "Success Visits",
      data:
        Data?.data?.metricsData?.VisitCount?.map(
          (v: { SuccessVisits: any }) => v.SuccessVisits
        ) || [],
      backgroundColor: "#00e5b5",
    },
  ];

  const callsLabels =
    Data?.data?.metricsData?.CallCount?.map((c: { Year: any }) => c.Year) || [];
  const callsDatasets = [
    {
      label: "Total Calls",
      data:
        Data?.data?.metricsData?.CallCount?.map(
          (c: { TotalCalls: any }) => c.TotalCalls
        ) || [],
      backgroundColor: "#2196f3",
    },
    {
      label: "Success Calls",
      data:
        Data?.data?.metricsData?.CallCount?.map(
          (c: { SuccessCalls: any }) => c.SuccessCalls
        ) || [],
      backgroundColor: "#00e5b5",
    },
  ];
  const roasterLabels =
    Data?.data?.metricsData?.RosterCount?.map((r: { Year: any }) => r.Year) ||
    [];
  const roasterDatasets = [
    {
      label: "Planned",
      data:
        Data?.data?.metricsData?.RosterCount?.map(
          (r: { TotalRosterPlanned: any }) => r.TotalRosterPlanned
        ) || [],
      backgroundColor: "#2196f3",
    },
    {
      label: "Visited",
      data:
        Data?.data?.metricsData?.RosterCount?.map(
          (r: { TotalRosterPlannedVisited: any }) => r.TotalRosterPlannedVisited
        ) || [],
      backgroundColor: "#00e5b5",
    },
  ];
  const CallObjectiveTotalCountLabels =
    Data?.data?.metricsData?.CallObjectiveTotalCount?.map(
      (r: { VisitCallPurpose: any }) => r.VisitCallPurpose
    ) || [];
  const CallObjectiveTotalCountDatasets = [
    {
      label: "Call Purpose Count",
      data:
        Data?.data?.metricsData?.CallObjectiveTotalCount?.map(
          (r: { CallPurposeCount: any }) => r.CallPurposeCount
        ) || [],
      backgroundColor: "#2196f3",
    },
  ];
  const VisitObjectiveTotalCountLabels =
    Data?.data?.metricsData?.VisitObjectiveTotalCount?.map(
      (r: { VisitCallPurpose: any }) => r.VisitCallPurpose
    ) || [];
  const VisitObjectiveTotalCountDatasets = [
    {
      label: "Visit Purpose Count",
      data:
        Data?.data?.metricsData?.VisitObjectiveTotalCount?.map(
          (r: { VisitPurposeCount: any }) => r.VisitPurposeCount
        ) || [],
      backgroundColor: "#2196f3",
    },
  ];
  const VisitStatusCountLabels =
    Data?.data?.metricsData?.VisitStatusCount?.map(
      (v: { StatusName: any }) => v.StatusName
    ) || [];

  const VisitStatusCountDatasets = [
    {
      label: "Visits",
      data:
        Data?.data?.metricsData?.VisitStatusCount?.map(
          (v: { VisitCount: any }) => v.VisitCount
        ) || [],
      backgroundColor: ["#2196f3", "#00e5b5", "#ff9800", "#f44336"],
    },
  ];
  const CallLogStatusCountLabels =
    Data?.data?.metricsData?.CallLogStatusCount?.map(
      (v: { StatusName: any }) => v.StatusName
    ) || [];

  const CallLogStatusCountDatasets = [
    {
      label: "Visits",
      data:
        Data?.data?.metricsData?.CallLogStatusCount?.map(
          (v: { CallCount: any }) => v.CallCount
        ) || [],
      backgroundColor: ["#2196f3", "#00e5b5", "#ff9800", "#f44336"],
    },
  ];
  const WonAndLostOpportunityLabels =
    Data?.data?.opportunitymetricsData?.WonAndLostOpportunity?.map(
      (v: { Year: string }) => v.Year
    ) || [];

  const WonAndLostOpportunityDatasets = [
    {
      label: "Won",
      data:
        Data?.data?.opportunitymetricsData?.WonAndLostOpportunity?.map(
          (c: { WonPercentage: number }) => c.WonPercentage
        ) || [],
      borderColor: "#2196f3",
      backgroundColor: "#2196f3",
      tension: 0.4,
    },
    {
      label: "Lost",
      data:
        Data?.data?.opportunitymetricsData?.WonAndLostOpportunity?.map(
          (c: { LostPercentage: number }) => c.LostPercentage
        ) || [],
      borderColor: "#00e5b5",
      backgroundColor: "#00e5b5",
      tension: 0.4,
    },
  ];

  const CurrentOpportunityInStagesLabels =
    Data?.data?.opportunitymetricsData?.CurrentOpportunityInStages?.map(
      (v: {
        Status: string;
        CurrentOpportunityCount: number;
        TotalCurrentOpportunityValue: number;
      }) =>
        `${v.Status} ${v.CurrentOpportunityCount} (₹ ${v.TotalCurrentOpportunityValue})`
    ) || [];

  const CurrentOpportunityInStagesDatasets = [
    {
      label: "Current Opportunity",
      data:
        Data?.data?.opportunitymetricsData?.CurrentOpportunityInStages?.map(
          (v: { TotalCurrentOpportunityValue: number }) =>
            v.TotalCurrentOpportunityValue
        ) || [],
      backgroundColor: ["#ff726f", "#4fc3f7", "#ffccbc"],
    },
  ];
  const DueOpportunityInStagesLabels =
    Data?.data?.opportunitymetricsData?.DueOpportunityInStages?.map(
      (v: {
        Status: string;
        DueOpportunityCount: number;
        TotalDueOpportunityValue: number;
      }) =>
        `${v.Status} ${v.DueOpportunityCount} (₹ ${v.TotalDueOpportunityValue})`
    ) || [];

  const DueOpportunityInStagesDatasets = [
    {
      label: "Due Opportunity",
      data:
        Data?.data?.opportunitymetricsData?.DueOpportunityInStages?.map(
          (v: { TotalDueOpportunityValue: number }) =>
            v.TotalDueOpportunityValue
        ) || [],
      backgroundColor: ["#ff726f", "#4fc3f7", "#ffccbc"],
    },
  ];
  const OpportunitiesCountLabels =
    Data?.data?.opportunitymetricsData?.OpportunitiesCount?.map(
      (c: { Year: any }) => c.Year
    ) || [];
  const OpportunitiesCountDatasets = [
    {
      label: "Count",
      data:
        Data?.data?.opportunitymetricsData?.OpportunitiesCount?.map(
          (c: { OpportunityCount: any }) => c.OpportunityCount
        ) || [],
      backgroundColor: "#2196f3",
    },
    {
      label: "Value",
      data:
        Data?.data?.opportunitymetricsData?.OpportunitiesCount?.map(
          (c: { OpportunityValue: any }) => c.OpportunityValue
        ) || [],
      backgroundColor: "#00e5b5",
    },
  ];
  const AllOpportunityTagsLabels =
    Data?.data?.opportunitymetricsData?.AllOpportunityTags?.map(
      (c: { TagName: any }) => c.TagName
    ) || [];
  const AllOpportunityTagsDatasets = [
    {
      label: "Count",
      data:
        Data?.data?.opportunitymetricsData?.AllOpportunityTags?.map(
          (c: { AllOpportunityCount: any }) => c.AllOpportunityCount
        ) || [],
      backgroundColor: "#2196f3",
    },
    {
      label: "Value",
      data:
        Data?.data?.opportunitymetricsData?.AllOpportunityTags?.map(
          (c: { TotalAllOpportunityValue: any }) => c.TotalAllOpportunityValue
        ) || [],
      backgroundColor: "#00e5b5",
    },
  ];
  const WonOpportunityTagsLabels =
    Data?.data?.opportunitymetricsData?.WonOpportunityTags?.map(
      (c: { TagName: any }) => c.TagName
    ) || [];
  const WonOpportunityTagsDatasets = [
    {
      label: "Count",
      data:
        Data?.data?.opportunitymetricsData?.WonOpportunityTags?.map(
          (c: { OpportunityWonCount: any }) => c.OpportunityWonCount
        ) || [],
      backgroundColor: "#2196f3",
    },
    {
      label: "Value",
      data:
        Data?.data?.opportunitymetricsData?.WonOpportunityTags?.map(
          (c: { TotalWonOpportunityValue: any }) => c.TotalWonOpportunityValue
        ) || [],
      backgroundColor: "#00e5b5",
    },
  ];
  const LostOpportunityTagsLabels =
    Data?.data?.opportunitymetricsData?.LostOpportunityTags?.map(
      (c: { TagName: any }) => c.TagName
    ) || [];
  const LostOpportunityTagsDatasets = [
    {
      label: "Count",
      data:
        Data?.data?.opportunitymetricsData?.LostOpportunityTags?.map(
          (c: { OpportunityLostCount: any }) => c.OpportunityLostCount
        ) || [],
      backgroundColor: "#2196f3",
    },
    {
      label: "Value",
      data:
        Data?.data?.opportunitymetricsData?.LostOpportunityTags?.map(
          (c: { TotalLostOpportunityValue: any }) => c.TotalLostOpportunityValue
        ) || [],
      backgroundColor: "#00e5b5",
    },
  ];
  const ProductByCategoryLabels =
    Data?.data?.ordermetricsData?.ProductByCategory?.map(
      (c: { CategoryName: any }) => c.CategoryName
    ) || [];
  const ProductByCategoryDatasets = [
    {
      label: "Category",
      data:
        Data?.data?.ordermetricsData?.ProductByCategory?.map(
          (c: { TotalSales: any }) => c.TotalSales
        ) || [],
      backgroundColor: "#2196f3",
    },
  ];
  const OrderCancellationPercentageLabels =
    Data?.data?.ordermetricsData?.OrderCancellationPercentage?.map(
      (c: { Year: any }) => c.Year
    ) || [];
  const OrderCancellationPercentageDatasets = [
    {
      label: "Cancellation (%)",
      data:
        Data?.data?.ordermetricsData?.OrderCancellationPercentage?.map(
          (c: { OrderCancellationRate: any }) => c.OrderCancellationRate
        ) || [],
      backgroundColor: "#2196f3",
    },
  ];
  const TotalDiscountPercentageLabels =
    Data?.data?.ordermetricsData?.TotalDiscountPercentage?.map(
      (c: { Year: string }) => c.Year
    ) || [];

  const TotalDiscountPercentageDatasets = [
    {
      label: "Discount (%)",
      data:
        Data?.data?.ordermetricsData?.TotalDiscountPercentage?.map(
          (c: { OrderDiscountPercentage: string }) =>
            parseFloat(c.OrderDiscountPercentage.replace("%", "").trim()) || 0
        ) || [],
      backgroundColor: "#2196f3",
    },
  ];

  const MaxMinOpportunityData =
    Data?.data.opportunitymetricsData?.MaxMinOpportunity[0];
  const LatestOpportunity =
    Data?.data.opportunitymetricsData?.LatestOpportunity[0];
  const LastUpdatedSinces =
    Data?.data.opportunitymetricsData?.LastUpdatedSinces[0];
  const MaxMinOrderData = Data?.data.ordermetricsData?.MaxMinOrder;
  const lastOrderData = Data?.data.ordermetricsData?.LastOrderModel;

  const TotalOrderAmountByYearLabels =
    Data?.data?.ordermetricsData?.TotalOrderAmountByYear?.map(
      (c: { Year: any }) => c.Year
    ) || [];
  const TotalOrderAmountByYearDatasets = [
    {
      label: "Value",
      data:
        Data?.data?.ordermetricsData?.TotalOrderAmountByYear?.map(
          (c: { Amount: any }) => c.Amount
        ) || [],
      backgroundColor: "#00e5b5",
    },
  ];
  const transformOrderByStatus = (data: any) => {
    if (!data) return { labels: [], datasets: [] };

    const orderMapping = [
      {
        label: "Pending",
        countKey: "TotalPendingOrderCount",
        amountKey: "TotalPendingOrderAmount",
      },
      {
        label: "Completed",
        countKey: "TotalCompleteOrderCount",
        amountKey: "TotalCompleteOrderAmount",
      },
      {
        label: "Accepted",
        countKey: "TotalAcceptOrderCount",
        amountKey: "TotalAcceptOrderAmount",
      },
      {
        label: "Partially Completed",
        countKey: "TotalPartialCompleteOrderCount",
        amountKey: "TotalPartialCompleteOrderAmount",
      },
      {
        label: "Partially Shipped",
        countKey: "TotalPartialShippedOrderCount",
        amountKey: "TotalPartialShippedOrderAmount",
      },
      {
        label: "Shipped",
        countKey: "TotalShippedOrderCount",
        amountKey: "TotalShippedOrderAmount",
      },
      {
        label: "Partially Delivered",
        countKey: "TotalPartialDeliveredOrderCount",
        amountKey: "TotalPartialDeliveredOrderAmount",
      },
      {
        label: "Closed",
        countKey: "TotalClosedOrderCount",
        amountKey: "TotalClosedOrderAmount",
      },
    ];

    const labels = orderMapping.map((item) => item.label);

    const countDataset = {
      label: "Order Count",
      data: orderMapping.map((item) => data?.[item.countKey] ?? 0),
      backgroundColor: "#6B68B3",
    };

    const amountDataset = {
      label: "Order Amount",
      data: orderMapping.map((item) => data?.[item.amountKey] ?? 0),
      backgroundColor: "#B0B2E8",
    };
    return { labels, datasets: [countDataset, amountDataset] };
  };

  const { labels, datasets } = useMemo(() => {
    const orderStatus = Data?.data?.ordermetricsData?.OrderStatus ?? {};
    return transformOrderByStatus(orderStatus);
  }, [Data?.data?.ordermetricsData?.OrderStatus]);
  const transformVisitDynamicForm = (
    LeadDynamicFormsCount = [],
    LeadVisitFormsCount = []
  ) => {
    if (!LeadDynamicFormsCount?.length && !LeadVisitFormsCount?.length)
      return null;

    const allYears = Array.from(
      new Set([
        ...LeadDynamicFormsCount.map((res: any) => res.Year),
        ...LeadVisitFormsCount.map((res: any) => res.Year),
      ])
    );

    const dynamicList: any = [...LeadDynamicFormsCount];
    const visitList: any = [...LeadVisitFormsCount];

    allYears.forEach((year: any) => {
      if (!dynamicList.some((obj: any) => obj.Year === year)) {
        dynamicList.push({ Year: year, DynamicFormsCount: 0 });
      }
      if (!visitList.some((obj: any) => obj.Year === year)) {
        visitList.push({ Year: year, VisitFormsCount: 0 });
      }
    });

    dynamicList.sort(
      (a: { Year: string }, b: { Year: string }) =>
        parseInt(a.Year) - parseInt(b.Year)
    );
    visitList.sort(
      (a: { Year: string }, b: { Year: string }) =>
        parseInt(a.Year) - parseInt(b.Year)
    );

    const labels = dynamicList.map((obj: { Year: any }) => obj.Year);
    const dynamicData = dynamicList.map(
      (obj: { DynamicFormsCount: any }) => obj.DynamicFormsCount
    );
    const visitData = visitList.map(
      (obj: { VisitFormsCount: any }) => obj.VisitFormsCount
    );

    return {
      labels,
      datasets: [
        {
          label: "Dynamic Form",
          data: dynamicData,
          backgroundColor: "#0095ff",
        },
        {
          label: "Visit Form",
          data: visitData,
          backgroundColor: "#00e096",
        },
      ],
    };
  };
  const { labels: labelsTaskMetrics, datasets: datasetsTaskMetrics } =
    transformVisitDynamicForm(
      Data?.data?.LeadDynamicFormsCount,
      Data?.data?.LeadVisitFormsCount
    ) || { labels: [], datasets: [] };

  const transformTaskStatusData = (data: any) => {
    if (!data) {
      return {
        taskStatusData: { labels: [], datasets: [] },
        overdueTask: { labels: [], datasets: [] },
        archivedTask: { labels: [], datasets: [] },
      };
    }

    // Task Status Data
    const taskStatusData = {
      labels: ["Pending", "Completed"],
      datasets: [
        {
          label: "Tasks",
          data: [data.PendingTasksCount, data.CompletedTasksCount],
          backgroundColor: "#FBBC04",
        },
      ],
    };

    // Overdue Tasks
    const overdueTask = { labels: [] as any, datasets: [] as any[] };
    if (data.OverduePendingTasksCount !== 0 || data.PendingTasksCount !== 0) {
      const overdueData = [
        data.OverduePendingTasksCount,
        data.PendingTasksCount - data.OverduePendingTasksCount,
      ];
      overdueTask.labels = [
        `Overdue (${overdueData[0]})`,
        `On Time (${overdueData[1]})`,
      ];
      overdueTask.datasets = [
        {
          label: "Overdue Tasks",
          data: overdueData,
          backgroundColor: "#EA4335",
        },
      ];
    }

    // Archived Tasks
    const archivedTask = { labels: [] as any, datasets: [] as any[] };
    if (data.ArchivedTasksCount !== 0 || data.PendingTasksCount !== 0) {
      const archivedData = [
        data.ArchivedTasksCount,
        data.PendingTasksCount + data.CompletedTasksCount,
      ];
      archivedTask.labels = [
        `Archived (${archivedData[0]})`,
        `Current Task (${archivedData[1]})`,
      ];
      archivedTask.datasets = [
        {
          label: "Archived vs Current",
          data: archivedData,
          backgroundColor: "#D97F7F",
        },
      ];
    }

    return { taskStatusData, overdueTask, archivedTask };
  };

  const { taskStatusData, overdueTask, archivedTask } = transformTaskStatusData(
    Data?.data?.TaskMetrics
  );
  const transformBusinessMetrics = (data: any) => {
    const safeData = data ?? {
      TotalBusiness: 0,
      TotalTransactionValue: 0,
      TotalReferralReward: 0,
    };

    return {
      labels: [
        "Total Business",
        "Total Transaction Value",
        "Total Referral Reward",
      ],
      datasets: [
        {
          label: "Business Metrics",
          data: [
            safeData.TotalBusiness,
            safeData.TotalTransactionValue,
            safeData.TotalReferralReward,
          ],
          backgroundColor: "#0095ff",
        },
      ],
    };
  };

  const { labels: businessLabels, datasets: businessDatasets } =
    transformBusinessMetrics(Data?.data?.referralList || []);

  if (permissionLoading) {
    return null;
  }

  return (
    <Box
      sx={{
        width: fullHeight ? "100%" : "auto",
        flex: fullHeight ? 1 : undefined,
        display: fullHeight ? "flex" : "block",
        flexDirection: fullHeight ? "column" : undefined,
        minHeight: fullHeight ? 0 : undefined,
        bgcolor: "#fff",
        borderRadius: fullHeight ? 0 : 2,
        boxShadow: fullHeight ? 0 : 1,
      }}
      className={fullHeight ? undefined : "mx-6"}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          borderBottom: "1px solid #ddd",
          flexShrink: 0,
        }}
      >
        <Tabs
          value={tab}
          onChange={handleChange}
          textColor="primary"
          indicatorColor="primary"
          sx={{
            flex: 1,
            "& .MuiTab-root": {
              textTransform: "none",
              fontSize: {
                xs: "11px",
                sm: "12px",
                md: "14px",
              },
              minWidth: 0,
              padding: {
                xs: "8px 4px",
                sm: "8px 12px",
              },
            },
          }}
        >
          <Tab label="Normal Year" value="normal" />
          <Tab label="Financial Year" value="financial" />
        </Tabs>
        {fullHeight && (
          <IconButton
            onClick={fetchMetrics}
            aria-label="Refresh metrics"
            sx={{ mr: 1 }}
          >
            <RefreshIcon />
          </IconButton>
        )}
      </Box>

      <Box
        sx={{
          p: fullHeight ? 1 : 0.8,
          overflowY: "auto",
          flex: fullHeight ? 1 : undefined,
          height: fullHeight ? undefined : "73vh",
        }}
      >
        <SectionHeader title="Visit and Call Metrics" />

        {isFetching || !Data ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 1 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Opportunity */}
            <OpportunityMetrics
              visitLabels={visitLabels}
              visitDatasets={visitDatasets}
              callsLabels={callsLabels}
              callsDatasets={callsDatasets}
              roasterLabels={roasterLabels}
              roasterDatasets={roasterDatasets}
              VisitObjectiveTotalCountLabels={VisitObjectiveTotalCountLabels}
              VisitObjectiveTotalCountDatasets={
                VisitObjectiveTotalCountDatasets
              }
              CallObjectiveTotalCountLabels={CallObjectiveTotalCountLabels}
              CallObjectiveTotalCountDatasets={CallObjectiveTotalCountDatasets}
              VisitStatusCountLabels={VisitStatusCountLabels}
              VisitStatusCountDatasets={VisitStatusCountDatasets}
              CallLogStatusCountLabels={CallLogStatusCountLabels}
              CallLogStatusCountDatasets={CallLogStatusCountDatasets}
              upcomingVisitsColumn={upcomingVisitsColumn}
              Data={Data}
              columns={columns}
              opportunityFollowUpsColumns={opportunityFollowUpsColumns}
              lostReasonColumns={lostReasonColumns}
              WonAndLostOpportunityLabels={WonAndLostOpportunityLabels}
              WonAndLostOpportunityDatasets={WonAndLostOpportunityDatasets}
              CurrentOpportunityInStagesLabels={
                CurrentOpportunityInStagesLabels
              }
              CurrentOpportunityInStagesDatasets={
                CurrentOpportunityInStagesDatasets
              }
              DueOpportunityInStagesLabels={DueOpportunityInStagesLabels}
              DueOpportunityInStagesDatasets={DueOpportunityInStagesDatasets}
              OpportunitiesCountLabels={OpportunitiesCountLabels}
              OpportunitiesCountDatasets={OpportunitiesCountDatasets}
              AllOpportunityTagsLabels={AllOpportunityTagsLabels}
              AllOpportunityTagsDatasets={AllOpportunityTagsDatasets}
              WonOpportunityTagsLabels={WonOpportunityTagsLabels}
              WonOpportunityTagsDatasets={WonOpportunityTagsDatasets}
              LostOpportunityTagsLabels={LostOpportunityTagsLabels}
              LostOpportunityTagsDatasets={LostOpportunityTagsDatasets}
              MaxMinOpportunityData={MaxMinOpportunityData}
              LatestOpportunity={LatestOpportunity}
              LastUpdatedSinces={LastUpdatedSinces}
              singleColumn={!!fullHeight}
            />

            {/* Order */}
            <OrderMetrics
              TotalOrderAmountByYearLabels={TotalOrderAmountByYearLabels}
              TotalOrderAmountByYearDatasets={TotalOrderAmountByYearDatasets}
              labels={labels}
              datasets={datasets}
              mostOrderedColumn={mostOrderedColumn}
              mostOrderedProductList={Data?.data?.mostOrderedProductList || []}
              ProductByCategoryLabels={ProductByCategoryLabels}
              ProductByCategoryDatasets={ProductByCategoryDatasets}
              OrderCancellationPercentageLabels={
                OrderCancellationPercentageLabels
              }
              OrderCancellationPercentageDatasets={
                OrderCancellationPercentageDatasets
              }
              TotalDiscountPercentageLabels={TotalDiscountPercentageLabels}
              TotalDiscountPercentageDatasets={TotalDiscountPercentageDatasets}
              MaxMinOrderData={MaxMinOrderData}
              lastOrderData={lastOrderData}
              singleColumn={!!fullHeight}
            />

            {/* Forms & Task Metrics */}
            <TaskMetrics
              visitFormLabels={labelsTaskMetrics}
              visitFormDatasets={datasetsTaskMetrics}
              taskStatusLabels={taskStatusData.labels}
              taskStatusDatasets={taskStatusData.datasets}
              overdueTaskLabels={overdueTask.labels}
              overdueTaskDatasets={overdueTask.datasets}
              archivedTaskLabels={archivedTask.labels}
              archivedTaskDatasets={archivedTask.datasets}
              singleColumn={!!fullHeight}
            />

            {/* Referral Metrics */}
            <Referral
              businessLabels={businessLabels}
              businessDatasets={businessDatasets}
            />
          </>
        )}
      </Box>
    </Box>
  );
};

export default Insights;
